/**
 * MD-14 FE-LANDINGS — currency picker logic (Strategy B per ARCH §10)
 *
 * Resolution order:
 *   1. localStorage.asklaw_currency (client-side, takes precedence)
 *   2. cookie asklaw_currency (server hint, persisted via footer switcher)
 *   3. geo + accept-language detect:
 *        country === 'VN' && lang.startsWith('vi') → VND
 *        country !== 'VN' || lang.startsWith('en') → USD
 *        VN+en or non-VN+vi (ambiguous) → AMBIGUOUS (FE renders inline picker)
 *
 * `detectCurrency` is pure — caller (worker handler) supplies cf country +
 * Accept-Language. Test injects fixtures.
 */

export type Currency = 'VND' | 'USD';
export type CurrencyResolution =
  | { kind: 'resolved'; currency: Currency; reason: 'storage' | 'cookie' | 'geo' }
  | { kind: 'ambiguous'; suggested: Currency; reason: 'vn_en' | 'non_vn_vi' };

export type DetectInput = {
  storage_value?: Currency | null;
  cookie_value?: Currency | null;
  cf_country?: string | null;
  accept_language?: string | null;
};

export function detectCurrency(input: DetectInput): CurrencyResolution {
  if (input.storage_value === 'VND' || input.storage_value === 'USD') {
    return { kind: 'resolved', currency: input.storage_value, reason: 'storage' };
  }
  if (input.cookie_value === 'VND' || input.cookie_value === 'USD') {
    return { kind: 'resolved', currency: input.cookie_value, reason: 'cookie' };
  }

  const country = (input.cf_country ?? '').toUpperCase();
  const accept = (input.accept_language ?? '').toLowerCase();
  const isVN = country === 'VN';
  const isVi = accept.startsWith('vi') || accept.includes(',vi');
  const isEn = accept.startsWith('en') || accept.includes(',en');

  if (isVN && isVi) return { kind: 'resolved', currency: 'VND', reason: 'geo' };
  if (!isVN && isEn) return { kind: 'resolved', currency: 'USD', reason: 'geo' };
  if (isVN && isEn) return { kind: 'ambiguous', suggested: 'VND', reason: 'vn_en' };
  if (!isVN && isVi) return { kind: 'ambiguous', suggested: 'USD', reason: 'non_vn_vi' };

  // Defensive default — non-VN visitor more likely (per F-FE-LANDINGS §5)
  return { kind: 'resolved', currency: 'USD', reason: 'geo' };
}

/**
 * Render Set-Cookie header value for currency persistence (1y TTL).
 */
export function buildCurrencyCookie(currency: Currency, secure = true): string {
  const attrs = [
    `asklaw_currency=${currency}`,
    'Path=/',
    'Max-Age=31536000',
    'SameSite=Lax',
  ];
  if (secure) attrs.push('Secure');
  return attrs.join('; ');
}
